import { useState, useEffect } from 'react';
import { CircleCheck, GraduationCap, Video, Play } from 'lucide-react';
import { Headphones } from '../../components/CustomIcons';
import { Link } from 'react-router-dom';
import thumbnailImg from '../../assets/images/course_thumbnail.png';
import { apiUrls, imageUrl as imgUrl } from '../../utils/url';
import { postRequest } from '../../utils/networking';

function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  let path = normalized;
  if (path.startsWith('/')) path = path.substring(1);
  const assetsIndex = path.indexOf("assets/");
  if (assetsIndex !== -1) return path.substring(assetsIndex + 7);
  const asstesIndex = path.indexOf("asstes/");
  if (asstesIndex !== -1) return path.substring(asstesIndex + 7);
  if (path.includes("home/")) return path.replace("home/", "");
  return path;
}

const getProcessedImageUrl = (imageUrlSource) => {
  const FALLBACK_IMAGE = thumbnailImg;
  if (!imageUrlSource) return FALLBACK_IMAGE;
  if (imageUrlSource.startsWith('http')) return imageUrlSource;
  const extractedPath = getPath(imageUrlSource);
  if (!extractedPath) return FALLBACK_IMAGE;
  const cleanPath = extractedPath.replace(/^\/+/, '');
  return `${imgUrl}/${cleanPath}`;
};

export default function CourseHighlight() {
  const [courseData, setCourseData] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await postRequest(apiUrls.getcourse, {});
        console.log("getcourse API response in CourseHighlight:", data);
        if (data && data.status === "S" && data.data?.length > 0) {
          setCourseData(data.data[0]); // Grab the first course
        }
      } catch (error) {
        console.error("Course fetch error:", error);
      }
    };
    fetchCourses();
  }, []);

  const benefits = [
    "Amazon, Flipkart, Meesho Selling",
    "Product Research & Selection",
    "Listing Optimization",
    "Order Processing & Customer Service",
    "Live Projects & Case Studies",
    "Lifetime Access & Support"
  ];

  const features = [
    {
      icon: <GraduationCap className="w-6 h-6 text-orange-500" />,
      title: "45+ Hours",
      desc: "Video Content"
    },
    {
      icon: <Video className="w-6 h-6 text-orange-500" />,
      title: "Live Doubt",
      desc: "Clearing Sessions"
    },
    {
      icon: <Headphones className="w-6 h-6 text-orange-500" />,
      title: "Lifetime",
      desc: "Support"
    }
  ];

  return (
    <section className="py-16 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          
          {/* Left Side: Video Placeholder */}
          <div className="w-full lg:w-1/2">
            <div className="relative aspect-video lg:aspect-square max-h-[500px] w-full rounded-[2rem] overflow-hidden group cursor-pointer">
              <img src={courseData?.imgurl ? getProcessedImageUrl(courseData.imgurl) : thumbnailImg} alt="E-Commerce Made Simple Course" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </div>

          {/* Right Side: Content */}
          <div className="w-full lg:w-1/2">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Our E-Commerce Course
            </h2>
            <div className="w-16 h-1 bg-orange-500 rounded-full mb-6"></div>
            
            <p className="text-gray-600 text-base md:text-lg mb-8 leading-relaxed">
              A complete practical training program in Tamil to help you build, launch and grow your e-commerce business.
            </p>

            <div className="flex flex-col md:flex-row gap-8">
              {/* List items */}
              <div className="flex-1 space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CircleCheck className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" fill="currentColor" stroke="white" />
                    <span className="text-gray-700 font-medium text-sm md:text-base">{benefit}</span>
                  </div>
                ))}

                <Link to="/login" className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1 group">
                  View Course Details
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>

              {/* Highlight cards */}
              <div className="w-full md:w-[220px] shrink-0 bg-orange-50/50 rounded-2xl p-6 flex flex-col gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm leading-tight">{feature.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{feature.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
